import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  parseAsStringLiteral,
  type inferParserType,
} from 'nuqs';
import type { LayoutId, GroupPresetId } from '../../../../src/shared/view-system/registries';
import { LAYOUT_REGISTRY, GROUP_PRESET_REGISTRY } from '../../../../src/shared/view-system/registries';

// Naming conventions:
// - {resource}Id: identifiers (accountId, keystoneConversationId)
// - {resource}Status: status filters (artifactStatus)
// - {feature}Mode: view/layout modes (layoutMode)

// Modal — only 'preferences' survives; legacy modal types deleted with their views

export const modalTypes = ['preferences'] as const;
export type ModalType = (typeof modalTypes)[number];

export const modalParams = {
  modal: parseAsStringLiteral(modalTypes),
};

// Account tab

export const accountTabValues = ['activity', 'signals', 'intelligence', 'chat', 'timeline', 'contact', 'open-loops', 'product-usage'] as const;

// ---------------------------------------------------------------------------
// Flow chat dock — shareable/deep-linkable chat URL (GTMOPS-2285 Track A).
//
// Moved here from `components/flow/EnrollQueue/flowChatLink.ts` (which
// re-exports both names so existing imports keep working) so the param
// definitions live alongside every other nuqs parser group, per this file's
// own "single source of truth" role.
//
// Deliberately NOT spread into `allParams` below: `allParams` is the
// resolvable-context-key surface for action triggers (ActionsMenu.tsx,
// ActionTriggerModal.tsx, ActionInputFieldsEditor.tsx — see
// `isKnownContextKey` and the admin contextKey dropdown). `flowChat`/
// `flowChatAccount` are dock-transport state, not action context — adding
// them to `allParams` would silently surface them as bindable contextKeys in
// the admin dropdown for no reason. Same rationale as `layoutParam`,
// `groupPresetParam`, `selectionParams`, `viewParams`, `accountRouteParams`,
// and `pipelineRouteParams` below, none of which are in `allParams` either.
// ---------------------------------------------------------------------------
export const FLOW_CHAT_URL_PARAM = 'flowChat';
export const FLOW_CHAT_ACCOUNT_URL_PARAM = 'flowChatAccount';

export const flowChatParams = {
  [FLOW_CHAT_URL_PARAM]: parseAsString,
  [FLOW_CHAT_ACCOUNT_URL_PARAM]: parseAsString,
};

// ---------------------------------------------------------------------------
// Flow request deep link — the in-app mirror of the bare
// `/flow/requests/:requestId` route (U13, plan D2).
//
// Value is the ROUTE id spelling (`ei-<int>` / `ex-<uuid>`, see
// `src/shared/flow/requests.ts`), never the FEED id — the same string the
// bare route and `terra_signals.source_link` carry, so the address bar is
// pasteable verbatim.
//
// Owned by `useDockUrlSync`'s WRITE/READ effects and read through nuqs
// ONLY, exactly like `flowChatParams` above: a second writer going through
// react-router's `useSearchParams` would rebuild the whole query string from
// a snapshot that never observed nuqs's `shallow: true` history patch and
// would silently drop `flowChat`/`flowChatAccount` (the collision documented
// at `useDockUrlSync.ts`'s chat-sync effects). Deliberately NOT spread
// into `allParams` — dock-transport state, not action contextKey surface.
// ---------------------------------------------------------------------------
export const FLOW_REQUEST_URL_PARAM = 'request';

export const flowRequestParams = {
  [FLOW_REQUEST_URL_PARAM]: parseAsString,
};

// ---------------------------------------------------------------------------
// Flow Notes deep link — `?notes=a:<sfId>` / `?notes=c:<sfId>` (U9c).
//
// Value is `noteTargetKey`'s spelling (`@shared/flow/notes`), the same string
// the Slack notification link (U5b) targets. This parser is deliberately a
// bare string: whether the value is a VALID target (prefix + Salesforce id) is
// the sync hook's call (`useNotesUrlSync`), which validates against the shared
// `NotesRecordIdSchema` rather than a second regex here.
//
// Owned by `useNotesUrlSync`
// (`components/flow/shell-next/notes/useNotesUrlSync.ts`) and read through
// nuqs ONLY, exactly like `flowChatParams`/`flowRequestParams` above: a second
// writer going through react-router's `useSearchParams` would rebuild the
// query string from a snapshot that never observed nuqs's `shallow: true`
// history patch and would silently drop nuqs-owned params.
//
// Deliberately NOT spread into `allParams` below — dock-transport state, not
// action contextKey surface (it would surface as a bindable contextKey in the
// admin dropdown for no reason). `flowPanelIds` is untouched.
// ---------------------------------------------------------------------------
export const FLOW_NOTES_URL_PARAM = 'notes';

export const flowNotesParams = {
  [FLOW_NOTES_URL_PARAM]: parseAsString,
};

// ---------------------------------------------------------------------------
// V2 Flow shell panels — deep-linkable Chat/Actions/Flow open state
// (plan `2026-09-30-005-v2-panel-container-plan.md`, U5/R11).
//
// `?panels=chat,actions` is the set of OPEN side panels and `?flowClosed=true`
// is the rep's explicit "close Flow" intent. Both serialize as
// present-or-absent: an empty open-set writes `panels` away entirely, and
// `flowClosed` only ever serializes as `true` or absent (a `false` value is
// never written, and reading one is a no-op).
//
// Owned by `useFlowPanelsUrlSync`
// (`components/flow/shell-next/panels/useFlowPanelsUrlSync.ts`) and read
// through nuqs ONLY, exactly like `flowChatParams`/`flowRequestParams` above:
// a second writer going through react-router's `useSearchParams` would
// rebuild the whole query string from a snapshot that never observed nuqs's
// `shallow: true` history patch and would silently drop params owned by a
// different nuqs consumer (the collision documented at
// `useDockUrlSync.ts`'s chat-sync effects, and just above at
// `flowRequestParams`).
//
// Deliberately NOT spread into `allParams` below — shell-transport state, not
// action contextKey surface. Adding them would silently surface `panels` and
// `flowClosed` as bindable contextKeys in the admin dropdown
// (ActionsMenu.tsx, ActionTriggerModal.tsx, ActionInputFieldsEditor.tsx) for
// no reason. Same rationale as `flowChatParams`, `flowRequestParams`,
// `layoutParam`, `groupPresetParam`, `selectionParams`,
// `viewParams`, `accountRouteParams`, and `pipelineRouteParams`.
//
// `flowPanelIds` is the one runtime spelling of U1's `FlowPanelId` union
// (`panels/flow-panels-layout.ts`, `'chat' | 'actions'`) and must stay
// structurally identical to it (pinned by `__tests__/urlParams.test.ts`).
// There is deliberately NO Zod schema for the id list: the nuqs parsers ARE
// the validation boundary for the URL, and a parallel schema would trip
// `terra/no-parallel-schema-and-type` for no gain.
// ---------------------------------------------------------------------------
export const FLOW_PANELS_URL_PARAM = 'panels';
export const FLOW_CLOSED_URL_PARAM = 'flowClosed';

export const flowPanelIds = ['chat', 'actions'] as const;

export const flowPanelsParams = {
  [FLOW_PANELS_URL_PARAM]: parseAsArrayOf(parseAsStringLiteral(flowPanelIds)),
  [FLOW_CLOSED_URL_PARAM]: parseAsBoolean,
};

// V4 view shell — split into layout selection vs navigation/selection

// Derived from registries — add new types/modes to the registry, not here
export const layoutValues = LAYOUT_REGISTRY.map(e => e.id) as unknown as readonly LayoutId[];

/** Type guard: checks that a string value is a known LayoutId. */
export function isLayoutId(value: string): value is LayoutId {
  return (layoutValues as readonly string[]).includes(value);
}

/** Layout selection — owned by useLayout hook */
export const layoutParam = {
  layout: parseAsStringLiteral(layoutValues),
};

// Derived from registry — add new presets to the registry, not here
const groupPresetValues = GROUP_PRESET_REGISTRY.map(e => e.id) as unknown as readonly GroupPresetId[];

/** Group preset selection — owned by useGroupPreset hook */
export const groupPresetParam = {
  preset: parseAsStringLiteral(groupPresetValues).withDefault('inbox'),
};

/** Navigation/selection state — sidebar group + selected artifact id.
 *
 * Naming: `selectedArtifact` (typed noun, no `Id` suffix) follows Jira's
 * `?selectedIssue=` and Figma's `?node-id=` precedent. The bare `selected`
 * was deliberately replaced before the rename shipped — typing the noun
 * frees us up to add `?selectedStage=` etc. on the same route later.
 *
 * Hard cut from production's `?item=<id>` — no backward-compat read; pre-rename
 * URLs in the wild render the list without the drawer. See
 * /tmp/v4-url-architecture.html for the full design. */
export const selectionParams = {
  group: parseAsString,
  selectedArtifact: parseAsString,
};

/** Composed alias for consumers that need all v4 URL params */
const v4Params = { ...layoutParam, ...groupPresetParam, ...selectionParams };

// ---------------------------------------------------------------------------
// V4 shared params — single flat definition all V4 routes share
// ---------------------------------------------------------------------------

export const viewParams = {
  preset: parseAsStringLiteral(groupPresetValues).withDefault('inbox'),
  status: parseAsStringLiteral(['active', 'archived'] as const).withDefault('active'),
  filter: parseAsString,
  layout: parseAsStringLiteral(layoutValues),
  selectedArtifact: parseAsString,
  group: parseAsString,
};

// Route-specific additions — composed at call site
export const accountRouteParams = {
  category: parseAsString,
  conversationId: parseAsString,
};

export const pipelineRouteParams = {
  stage: parseAsString,
  sort: parseAsStringLiteral(['priority', 'amount', 'close_date', 'fit_score'] as const).withDefault('priority'),
};

// ---------------------------------------------------------------------------
// Core account params — load-bearing for v4 action triggers.
//
// KEEP-ASSERTION: accountId is the only contextKey that production
// action_definitions bind. Dropping it (from allParams OR from
// pathSourcedContextKeys below) would silently break action triggers via
// ActionsMenu / ActionTriggerModal / ActionInputFieldsEditor.
// The test in __tests__/urlParams.test.ts asserts this contract holds.
// ---------------------------------------------------------------------------
const accountParams = {
  accountTab: parseAsStringLiteral(accountTabValues).withDefault('activity'),
  keystoneConversationId: parseAsString,
};

// ---------------------------------------------------------------------------
// Path-sourced context keys — resolved via route path only, never query string.
//
// accountId lives in the route PATH (`/accounts/:accountId`, `/pipeline/:accountId`),
// not the query string, so it is intentionally NOT a member of `allParams` —
// that would make it a live (but dead) query-string parser. `pathSourcedParams`
// exists only to compute `AllParams`'s type; `pathSourcedContextKeys` is the
// runtime registry consumers check against (see `mergeContextParams` below,
// `isAllParamsKey` in ActionsMenu.tsx, and the admin contextKey dropdown in
// ActionInputFieldsEditor.tsx).
// ---------------------------------------------------------------------------
const pathSourcedParams = {
  accountId: parseAsString,
};

export const pathSourcedContextKeys = Object.keys(pathSourcedParams) as (keyof typeof pathSourcedParams)[];

// V4 artifact status filter (shared across routes; kept separate from archived view)
const artifactStatusParam = {
  artifactStatus: parseAsStringLiteral(['active', 'archived'] as const).withDefault('active'),
};

// Combined — single source of truth for context-aware consumers (ActionsMenu,
// ActionTriggerModal, ActionInputFieldsEditor)
export const allParams = {
  ...modalParams,
  ...accountParams,
  ...artifactStatusParam,
  filter: parseAsString,
  ...v4Params,
};

export type AllParams = inferParserType<typeof allParams> & inferParserType<typeof pathSourcedParams>;

// ---------------------------------------------------------------------------
// Context resolution — query string + route path
//
// nuqs (`useQueryStates`) only reads the query STRING, but the load-bearing
// `accountId` context key lives in the route PATH (`/accounts/:accountId`,
// `/pipeline/:accountId`) — as does `accountTab`. Action contextKey pre-fill
// must see both, so callers overlay route path segments on top of the query
// params. Without this, an account-scoped action field renders "(empty)".
// ---------------------------------------------------------------------------

/** Runtime check: is `key` a resolvable context key — either a query param
 * (`allParams`) or a path-sourced one (`pathSourcedContextKeys`, e.g. accountId)? */
export function isKnownContextKey(key: string): key is keyof AllParams {
  return key in allParams || (pathSourcedContextKeys as readonly string[]).includes(key);
}

/**
 * Merge route path segments (`useParams()`) onto query params (`useQueryStates`).
 * Only path segments that are known context keys (query-param keys from
 * `allParams`, or path-sourced keys like accountId) and non-empty are
 * overlaid, and they win over the query value. Pure — the React glue lives in
 * `useContextParams`.
 */
export function mergeContextParams(
  query: inferParserType<typeof allParams>,
  route: Record<string, string | undefined>,
): AllParams {
  const merged: Record<string, unknown> = { ...query };
  for (const [key, value] of Object.entries(route)) {
    if (isKnownContextKey(key) && value != null && value !== '') {
      merged[key] = value;
    }
  }
  return merged as AllParams;
}
